import { useMemo } from 'react';
import type { Work } from '../../types/api';
import { CtaLink } from '../ui';
import { capitalize, formatYear, projectSummary } from '../../utils/text';
import BleedShowcase from './BleedShowcase';
import { clamp3, type ShowcaseEntry } from './showcase';

const MAX_TABS = 4;

function WorkDetails({ work }: { work: Work }) {
  const year = formatYear(work.completed_date);
  const isFuture = !!work.completed_date && Date.parse(work.completed_date) > Date.now();
  const meta = [
    { label: 'Lieu', value: work.location?.trim() },
    { label: 'Client', value: work.client?.trim() },
    { label: isFuture ? 'Prévu' : 'Année', value: year },
  ].filter((m): m is { label: string; value: string } => !!m.value);
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-[0.0625rem] text-ink">Réalisation à la une</div>
      <h3 className="mt-4 text-inherit font-sans text-[1.75rem] font-bold normal-case leading-[1.15] tracking-normal text-black md:text-[2.25rem]" style={clamp3}>{capitalize(work.title)}</h3>
      <p className="mt-5 text-[1rem] leading-relaxed text-black">{projectSummary(work.subtitle)}</p>
      <dl className="mt-6 space-y-2">
        {meta.map((m) => (
          <div key={m.label} className="flex gap-3 text-xs uppercase tracking-[0.0625rem]">
            <dt className="w-16 shrink-0 text-ink">{m.label}</dt>
            <dd className="font-semibold text-black">{m.value}</dd>
          </div>
        ))}
      </dl>
      <div className="mt-8"><CtaLink to={`/works/${work.id}`}>En savoir plus</CtaLink></div>
    </div>
  );
}

/**
 * « Ce que nous construisons »: une réalisation à la une par service (jusqu'à 4), en mise en page large
 * (photo qui déborde, texte, fiche lieu / client / année): les réalisations ont plus d'éléments à montrer.
 */
export default function ProjectShowcase({ works }: { works: Work[] }) {
  const entries: ShowcaseEntry[] = useMemo(() => {
    const seen = new Set<string>();
    const out: ShowcaseEntry[] = [];
    for (const w of works) {
      const key = w.service?.name?.trim() || w.id;
      if (seen.has(key) || !w.cover_image?.url) continue;
      seen.add(key);
      out.push({ id: w.id, label: w.service?.name?.trim() || capitalize(w.title), image: w.cover_image.url, alt: w.title, content: <WorkDetails work={w} /> });
      if (out.length === MAX_TABS) break;
    }
    return out;
  }, [works]);
  return <BleedShowcase eyebrow="Ce que nous construisons" heading="Des solutions complètes pour chaque projet" entries={entries} imageHeight="h-[26rem] sm:h-[34rem] lg:h-[40rem]" />;
}
